import React, { PureComponent } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { Map, TileLayer, ZoomControl, AttributionControl } from 'react-leaflet';
import $ from 'cash-dom';

import { arraysEqual } from '~/core/scripts/utils';
import Icon from '~/core/scripts/components/Icon';
import { disableMap, enableMap } from '~/core/scripts/map-components/utils';

import 'leaflet/dist/leaflet.css';

const Wrapper = styled.div`
  .leaflet-top,
  .leaflet-bottom {
    z-index: 500;
  }

  .map-label {
    color: white;
    font-size: 1.2rem;
    line-height: 1.1;
    pointer-events: none !important;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-shadow: 0 0 1px rgba(0, 0, 0, 1), 0 0 1px rgba(0, 0, 0, 0.8), 0 0 2px rgba(0, 0, 0, 1);
    user-select: none;
    font-family: ${(p) => p.theme.font.family.sans};
    text-align: center;
    height: 1px !important;
    width: 1px !important;
    margin: 0 !important;

    span {
      display: inline-block;
      transform: translateX(-50%) translateY(-50%);
    }
  }
`;

import { MapToggle } from '~/css-in-js/map.styles';

class BasisMap extends PureComponent {
  static propTypes = {
    center: PropTypes.array,
    zoom: PropTypes.number,
    minZoom: PropTypes.number,
    maxZoom: PropTypes.number,
    zoomSnap: PropTypes.number,
    zoomDelta: PropTypes.number,
    maxBounds: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    fitBounds: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    tileSize: PropTypes.number,
    zoomOffset: PropTypes.number,
    zoomControl: PropTypes.string,

    showGeocoder: PropTypes.bool,
    zoomAfterGeocode: PropTypes.number,

    showBasemap: PropTypes.bool,
    basemapUrl: PropTypes.string,
    basemapAttribution: PropTypes.string,

    showLabels: PropTypes.bool,
    zoomAnimation: PropTypes.bool,
    showMapToggleButton: PropTypes.bool,
    isFullScreen: PropTypes.bool,
    enableScroll: PropTypes.bool,
  };

  static defaultProps = {
    center: null,
    zoom: null,
    minZoom: 9,
    maxZoom: 15,
    zoomDelta: 1,
    zoomSnap: 0.2,
    maxBounds: null,
    fitBounds: null,
    tileSize: 256,
    zoomOffset: 0,
    zoomControl: 'bottomright',

    showBasemap: false,
    basemapUrl: '',
    basemapAttribution: '',
    attributionPrefix: '',

    showGeocoder: false,
    zoomAfterGeocode: 11,

    showLabels: true,
    showMapToggleButton: true,
    isFullScreen: false,
    enableScroll: true,
  };

  state = {
    isMapActive: true,
  };

  constructor() {
    super();

    // we only want to set fitBounds once
    this.initFitBounds = false;
    this.initMove = true;
    this.lastRoundedZoom = 0;
  }

  componentDidMount() {
    this.state.isMapActive = this.props.enableScroll;
    if ((!this.state.isMapActive && !this.props.isFullScreen) || !this.props.enableScroll) {
      disableMap(this.map.leafletElement);
    }
  }

  componentDidUpdate(prevProps) {
    if (!arraysEqual(this.props.geocoderLatLng, prevProps.geocoderLatLng)) {
      this.map.leafletElement.setView(this.props.geocoderLatLng, this.props.zoomAfterGeocode);
    }
  }

  renderZoomControl() {
    if (!this.props.zoomControl) {
      return null;
    }

    return <ZoomControl position={this.props.zoomControl} />;
  }

  renderBasemap() {
    if (!this.props.showBasemap) {
      return null;
    }

    return (
      <>
        <TileLayer
          url={this.props.basemapUrl}
          attribution={this.props.basemapAttribution}
          zoomOffset={this.props.zoomOffset}
          tileSize={this.props.tileSize}
        />
        {this.props.basemapAttribution && (
          <AttributionControl prefix={this.props.attributionPrefix}>
            {this.props.basemapAttribution}
          </AttributionControl>
        )}
      </>
    );
  }

  renderMapToggleButton() {
    if (!this.props.showMapToggleButton) {
      return null;
    }

    return (
      <MapToggle onClick={this.toggleMapActive}>
        {this.state.isMapActive ? (
          <>
            <Icon type="scroll" />
            <span>Scrollen aktivieren</span>
          </>
        ) : (
          <>
            <Icon type="move" />
            <span>Karte aktivieren</span>
          </>
        )}
      </MapToggle>
    );
  }

  toggleMapActive = () => {
    if (!this.map) {
      return false;
    }
    if (this.state.isMapActive) {
      disableMap(this.map.leafletElement);
    } else {
      enableMap(this.map.leafletElement);
    }

    const isMapActive = !this.state.isMapActive;
    this.setState({ isMapActive });
    $('html').toggleClass('is-map-active', isMapActive);
  };

  onZoom = (evt) => {
    this.onMove();

    $('html').removeClass(`map-zoom-${this.lastRoundedZoom}`);

    this.lastRoundedZoom = Math.round(evt.target._zoom);

    $('html').addClass(`map-zoom-${this.lastRoundedZoom}`);
  };

  onDrag = () => {
    this.onMove();
  };

  onMove() {
    if (this.initMove) {
      this.initMove = false;
      return false;
    }

    if (!this.props.isFullScreen) {
      return false;
    }

    document.querySelector('html').classList.add('map-moved');
  }

  render() {
    return (
      <Wrapper>
        <Map
          className="map"
          ref={(ref) => {
            this.map = ref;
          }}
          center={this.props.center}
          zoom={this.props.zoom}
          bounds={this.props.fitBounds}
          zoomControl={false}
          zoomDelta={this.props.zoomDelta}
          zoomSnap={this.props.zoomSnap}
          minZoom={this.props.minZoom}
          maxZoom={this.props.maxZoom}
          maxBounds={this.props.maxBounds}
          zoomAnimation={this.props.zoomAnimation}
          onZoomEnd={this.onZoom}
          onDragEnd={this.onDrag}
          attributionControl={false}
        >
          {this.renderBasemap()}
          {this.renderZoomControl()}
          {this.props.children}
          {this.renderMapToggleButton()}
        </Map>
      </Wrapper>
    );
  }
}

export default BasisMap;
